import 'bootstrap/dist/css/bootstrap.min.css';
import { BrowserRouter, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import Footer from './components/Footer/Footer';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { WindowSizeProvider } from './hooks/WindowSizeContext';
import { Home, Login, SignUp, Videos, ContractCamera, Livestream, LivestreamPlayer, FutSafe, Torneios, FastTennis, Sponsor, Gestor } from './pages';
import { isAdminUser } from './api/gestor';
import { createContext, useContext, useEffect, useState } from 'react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import { DrawerBody } from './components/Navbar/Navbar';
import ScrollToTopOnMount from './hooks/ScrollToTopOnMount';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './api/firebase';

interface ProtectedRouteProps {
  element: any;
}

function ProtectedRoute({ element }: ProtectedRouteProps) {
  const [status, setStatus] = useState<'loading' | 'auth' | 'noauth'>(
    () => (localStorage.getItem('accessToken') ? 'auth' : 'loading')
  );

  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      if (user) {
        user.getIdToken().then((token) => {
          localStorage.setItem('accessToken', token);
          setStatus('auth');
        });
      } else {
        localStorage.removeItem('accessToken');
        setStatus('noauth');
      }
    });
  }, []);

  if (status === 'loading') return null;
  if (status === 'noauth') return <Navigate to="/login" replace />;
  return element;
}

// Rota /gestor: somente o login do gestor (validado também no backend).
function AdminRoute({ element }: ProtectedRouteProps) {
  const [status, setStatus] = useState<'loading' | 'admin' | 'denied'>('loading');

  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      if (user && isAdminUser(user.email)) {
        setStatus('admin');
      } else if (user) {
        setStatus('denied');
      } else {
        setStatus('denied');
      }
    });
  }, []);

  if (status === 'loading') return null;
  if (status === 'denied') return <Navigate to="/login" replace />;
  return element;
}

const KNOWN_TOP_PATHS = [
  '', 'login', 'signup', 'videos', 'contract-camera',
  'livestream', 'futsafe', 'torneios', 'fasttennis', 'gestor',
];

function ConditionalFooter() {
  const location = useLocation();
  const isLoggedIn = !!localStorage.getItem('accessToken');
  // Páginas de patrocínio são a rota catch-all "/:arenaSlug": qualquer caminho
  // de um único segmento que não seja uma rota conhecida. Elas têm rodapé próprio.
  const segments = location.pathname.split('/').filter(Boolean);
  const isSponsorRoute = segments.length === 1 && !KNOWN_TOP_PATHS.includes(segments[0]);
  const hideOldFooter =
    (location.pathname === '/' && !isLoggedIn) ||
    location.pathname === '/contract-camera' ||
    location.pathname === '/login' ||
    location.pathname === '/signup' ||
    location.pathname === '/videos' ||
    location.pathname.startsWith('/livestream') ||
    location.pathname === '/futsafe' ||
    location.pathname === '/torneios' ||
    location.pathname === '/fasttennis' ||
    location.pathname === '/gestor' ||
    isSponsorRoute;
  if (hideOldFooter) return null;
  return <Footer />;
}

const DrawerContext = createContext<any>(null);

const App = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const toggleDrawer = () => {
    setIsDrawerOpen((prev) => !prev);
  };
  const queryClient = new QueryClient();

  return (
    <DrawerContext.Provider value={{ isDrawerOpen, toggleDrawer }}>
      <WindowSizeProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <ScrollToTopOnMount />
            <Drawer
              style={{ background: '#F2F2F2' }}
              open={isDrawerOpen}
              onClose={toggleDrawer}
              direction="right"
              size={275}>
              <DrawerBody />
            </Drawer>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/videos" element={<ProtectedRoute element={<Videos />} />} />
              <Route path="/contract-camera" element={<ContractCamera />} />
              <Route path="/livestream" element={<ProtectedRoute element={<Livestream />} />} />
              <Route path="/livestream/:id" element={<ProtectedRoute element={<LivestreamPlayer />} />} />
              <Route path="/futsafe" element={<FutSafe />} />
              <Route path="/torneios" element={<Torneios />} />
              <Route path="/fasttennis" element={<ProtectedRoute element={<FastTennis />} />} />
              <Route path="/gestor" element={<AdminRoute element={<Gestor />} />} />
              {/* Página pública de patrocínio por arena (catch-all — manter por último) */}
              <Route path="/:arenaSlug" element={<Sponsor />} />
            </Routes>
            <ConditionalFooter />
          </BrowserRouter>
        </QueryClientProvider>
      </WindowSizeProvider>
    </DrawerContext.Provider>
  );
};

export const useDrawer = () => {
  return useContext(DrawerContext);
};

export default App;
